import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import NumericTextField from './NumericTextField';
import DocumentPageOverlay from './DocumentPageOverlay';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from './PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const AdvisorInquiryForm = ({
  open,
  onClose,
  mode = 'package',
  partnerId,
  partnerName,
  teamMembers: teamMembersProp,
  packageId,
  packageTitle,
  selectedPricing,
  travelers: travelersProp,
  onTravelersChange,
  catalogToken,
  clientToken,
  referralCode,
  maxTravelers = 20,
  onTrackStarted,
  onInquiryCreated,
  fromDirectory = false,
  privateRequest = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [travelers, setTravelers] = useState(travelersProp || 2);
  const [phone, setPhone] = useState(user?.phone || '');
  const [destination, setDestination] = useState('');
  const [budget, setBudget] = useState('');
  const [customizationNotes, setCustomizationNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inquiryId, setInquiryId] = useState(null);
  const [teamMembers, setTeamMembers] = useState(teamMembersProp || []);
  const [requestedPartnerUserId, setRequestedPartnerUserId] = useState('');
  const [successAdvisorName, setSuccessAdvisorName] = useState('');
  const [accountEmailSent, setAccountEmailSent] = useState(false);
  const guestContact = !user && mode === 'general' && !privateRequest;

  useEffect(() => {
    if (!open) return;
    setInquiryId(null);
    setError('');
    setAccountEmailSent(false);
    setContactName('');
    setContactEmail('');
    setPhone(user?.phone || '');
    setRequestedPartnerUserId('');
    setSuccessAdvisorName('');
    if (typeof travelersProp === 'number' && travelersProp > 0) {
      setTravelers(travelersProp);
    }
    onTrackStarted?.();
  }, [open, user?.phone, travelersProp, onTrackStarted]);

  useEffect(() => {
    if (!open || !partnerId) {
      setTeamMembers(teamMembersProp || []);
      return;
    }
    if (teamMembersProp?.length) {
      setTeamMembers(teamMembersProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/package-booking/partners/${partnerId}/team-members`);
        if (!cancelled && response.data?.success) {
          setTeamMembers(response.data.team || []);
        }
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, partnerId, teamMembersProp]);

  const handleTravelers = (value) => {
    setTravelers(value);
    onTravelersChange?.(value);
  };

  const selectedTeamMemberName = requestedPartnerUserId
    ? teamMembers.find((member) => String(member.id) === String(requestedPartnerUserId))?.name
    : null;

  const handleSubmit = async () => {
    if (!user && !guestContact) {
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    if (!message.trim()) return;
    if ((mode === 'general' || privateRequest) && !destination.trim()) return;
    if (guestContact) {
      if (!contactName.trim() || !contactEmail.trim() || !phone.trim()) {
        setError(t('publicPackage.guestContactRequired'));
        return;
      }
    }
    try {
      setLoading(true);
      setError('');
      const payload = {
        message: message.trim(),
        travelers,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        phone: phone || undefined,
        referralCode,
        clientToken,
        catalogToken,
        fromDirectory,
      };
      if (requestedPartnerUserId && !privateRequest) {
        payload.partnerUserId = Number(requestedPartnerUserId);
      }
      if (mode === 'package') {
        payload.packageId = packageId;
        payload.pricingId = selectedPricing?.id;
        payload.customizationNotes = customizationNotes || undefined;
      } else if (privateRequest) {
        payload.privateRequest = true;
        payload.destination = destination || undefined;
        payload.budgetRange = budget || undefined;
      } else {
        payload.partnerId = partnerId;
        payload.destination = destination || undefined;
        payload.budgetRange = budget || undefined;
      }
      if (guestContact) {
        payload.name = contactName.trim();
        payload.email = contactEmail.trim();
        payload.phone = phone.trim();
      }
      const response = await api.post(guestContact ? '/package-booking/inquiry/guest' : '/package-booking/inquiry', payload);
      if (response.data?.success) {
        const createdId = response.data.inquiryId;
        setMessage('');
        setCustomizationNotes('');
        setSuccessAdvisorName(
          selectedTeamMemberName
          || response.data.requestedTeamMemberName
          || partnerName
          || t('publicPackage.contactAgent')
        );
        setAccountEmailSent(Boolean(response.data.accountEmailSent));
        if (onInquiryCreated) {
          onInquiryCreated(createdId);
        } else {
          setInquiryId(createdId);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('publicPackage.inquiryFailed'));
    } finally {
      setLoading(false);
    }
  };

  const title = privateRequest
    ? t('myInquiries.privateRequestTitle')
    : mode === 'package'
      ? t('publicPackage.askAboutTrip')
      : t('publicPackage.askThisAdvisor');

  return (
    <DocumentPageOverlay open={open} onClose={onClose} title={title} variant="compact" testId="advisor-inquiry-overlay">
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }} data-testid="advisor-inquiry-form">
        {inquiryId ? (
          <Alert severity="success" data-testid="inquiry-sent">
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>{t('publicPackage.inquirySentTitle')}</Typography>
            {privateRequest
              ? t('myInquiries.privateRequestSentBody')
              : t('publicPackage.inquirySentBody', { name: successAdvisorName })}
            {accountEmailSent ? (
              <Typography sx={{ mt: 1.5 }}>{t('publicPackage.guestInquirySentBody')}</Typography>
            ) : (
            <Box sx={{ mt: 1.5 }}>
              <Button
                component={RouterLink}
                to={`/my-inquiries/${inquiryId}`}
                sx={partnerOxbloodBtnSx}
              >
                {t('publicPackage.viewConversation')}
              </Button>
            </Box>
            )}
          </Alert>
        ) : (
          <Box>
            {privateRequest ? (
              <Typography sx={{ color: DOCUMENT.ink2, mb: 2.5 }}>
                {t('myInquiries.privateRequestLead')}
              </Typography>
            ) : packageTitle || partnerName ? (
              <Typography sx={{ color: DOCUMENT.ink2, mb: 2.5 }}>
                {packageTitle ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 600 }}>{packageTitle}</Box>
                    {partnerName ? ` · ${t('publicPackage.offeredBy', { name: partnerName })}` : null}
                  </>
                ) : t('publicPackage.offeredBy', { name: partnerName })}
              </Typography>
            ) : null}
            <Typography sx={{ color: DOCUMENT.ink3, mb: 3 }}>
              {privateRequest
                ? t('myInquiries.privateRequestHint')
                : mode === 'package'
                  ? t('publicPackage.inquiryHint')
                  : t('publicPackage.customRequestHint')}
            </Typography>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            <Grid container spacing={2}>
              {guestContact ? (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label={t('publicPackage.yourName')}
                      fullWidth
                      required
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      inputProps={{ 'data-testid': 'inquiry-guest-name' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('publicPackage.email')}
                      type="email"
                      fullWidth
                      required
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      inputProps={{ 'data-testid': 'inquiry-guest-email' }}
                    />
                  </Grid>
                </>
              ) : null}
              {!privateRequest && teamMembers.length > 0 ? (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="inquiry-team-member-label">
                      {t('publicPackage.requestedAdvisorLabel')}
                    </InputLabel>
                    <Select
                      labelId="inquiry-team-member-label"
                      label={t('publicPackage.requestedAdvisorLabel')}
                      value={requestedPartnerUserId}
                      onChange={(e) => setRequestedPartnerUserId(e.target.value)}
                      data-testid="inquiry-team-select"
                    >
                      <MenuItem value="">
                        {t('publicPackage.anyoneAtAgency', { company: partnerName || t('publicPackage.contactAgent') })}
                      </MenuItem>
                      {teamMembers.map((member) => (
                        <MenuItem key={member.id} value={String(member.id)}>
                          {member.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : null}
              {mode === 'general' ? (
                <Grid item xs={12}>
                  <TextField
                    label={t('publicPackage.destination')}
                    fullWidth
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                  />
                </Grid>
              ) : null}
              <Grid item xs={6}>
                <TextField
                  label={t('publicPackage.startDate')}
                  type="date"
                  fullWidth
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('publicPackage.endDate')}
                  type="date"
                  fullWidth
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumericTextField
                  label={t('publicPackage.travelers')}
                  fullWidth
                  value={travelers}
                  onValueChange={handleTravelers}
                  min={1}
                  max={maxTravelers}
                />
              </Grid>
              <Grid item xs={guestContact ? 12 : 6} sm={6}>
                <TextField
                  label={guestContact ? t('publicPackage.phoneRequired') : t('publicPackage.phone')}
                  fullWidth
                  required={guestContact}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputProps={{ 'data-testid': 'inquiry-phone' }}
                />
              </Grid>
              {mode === 'general' ? (
                <Grid item xs={12}>
                  <TextField
                    label={t('publicPackage.budget')}
                    fullWidth
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                  />
                </Grid>
              ) : null}
              {mode === 'package' && selectedPricing?.tier_name ? (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                    {t('publicPackage.selectedRate')}: {selectedPricing.tier_name}
                    {selectedPricing.price_per_person
                      ? ` · $${Number(selectedPricing.price_per_person).toLocaleString()}`
                      : ''}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <TextField
                  label={t('publicPackage.yourMessage')}
                  multiline
                  rows={4}
                  fullWidth
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('publicPackage.yourMessageHint')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {mode === 'package' ? (
                <Grid item xs={12}>
                  <TextField
                    label={t('publicPackage.customizationNotes')}
                    multiline
                    rows={2}
                    fullWidth
                    value={customizationNotes}
                    onChange={(e) => setCustomizationNotes(e.target.value)}
                    placeholder={t('publicPackage.customizationNotesHint')}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
          <Button onClick={onClose} sx={partnerOutlineBtnSx}>{t('common.cancel')}</Button>
          {!inquiryId ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !message.trim()
                || (mode === 'general' && !destination.trim())
                || (guestContact && (!contactName.trim() || !contactEmail.trim() || !phone.trim()))
                || loading
              }
              sx={{ ...partnerOxbloodBtnSx, minWidth: 140 }}
              data-testid="inquiry-send"
            >
              {loading ? <CircularProgress size={20} /> : t('publicPackage.sendRequest')}
            </Button>
          ) : null}
        </Box>
      </Box>
    </DocumentPageOverlay>
  );
};

export default AdvisorInquiryForm;
